import React, { useEffect, useState } from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { supabase } from './lib/supabase';
import { Session } from '@supabase/supabase-js';

// Components & Pages
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import JoinGroup from './pages/JoinGroup';
import TransactionForm from './pages/TransactionForm';
import History from './pages/History';
import Export from './pages/Export';
import Settings from './pages/Settings';
import Groups from './pages/Groups';
import GroupDetail from './pages/GroupDetail';
import Notes from './pages/Notes';
import Layout from './components/Layout';
import PinLock from './components/PinLock';

const App: React.FC = () => {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [pinVerified, setPinVerified] = useState(false);
  const [savedPin, setSavedPin] = useState<string | null>(null);
  const [fetchingPin, setFetchingPin] = useState(false);
  const isResetPasswordRoute = window.location.hash.startsWith('#/reset-password');
  const isReturningFromCameraCapture = sessionStorage.getItem('camera_capture_in_progress') === '1';

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) {
        handlePostAuth(session.user.id);
      } else {
        setLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session) {
        handlePostAuth(session.user.id);
      } else {
        setPinVerified(false);
        setSavedPin(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const handlePostAuth = async (userId: string) => {
    setFetchingPin(true);
    
    // 1. Check for Pending Group Invites
    const pendingInvite = sessionStorage.getItem('pending_invite_code');
    if (pendingInvite) {
      try {
        const { data: hh } = await supabase.from('households').select('id').eq('invite_code', pendingInvite).single();
        if (hh) {
          await supabase.from('household_members').upsert({ household_id: hh.id, user_id: userId });
          console.log("Auto-joined group via referral link");
        }
      } catch (e) {
        console.error("Auto-join failed", e);
      } finally {
        sessionStorage.removeItem('pending_invite_code');
      }
    }

    // 2. Fetch User Profile / PIN
    const { data } = await supabase.from('user_profiles').select('pin').eq('id', userId).single();
    setSavedPin(data?.pin || null);
    
    setLoading(false);
    setFetchingPin(false);
  };

  const handlePinSuccess = async (enteredPin: string) => {
    if (!savedPin) {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase.from('user_profiles').upsert({ id: user.id, pin: enteredPin });
        setSavedPin(enteredPin);
      }
    }
    setPinVerified(true);
  };

  if (loading || fetchingPin) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-400"></div>
      </div>
    );
  }

  // If logged in but PIN not verified, show PIN Wall
  if (session && !pinVerified && !isResetPasswordRoute && !isReturningFromCameraCapture) {
    return <PinLock savedPin={savedPin} onSuccess={handlePinSuccess} />;
  }

  return (
    <Router>
      <Routes>
        <Route path="/login" element={!session ? <Login /> : <Navigate to="/" />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/join/:code" element={<JoinGroup />} />
        
        <Route path="/" element={session ? <Layout><Dashboard /></Layout> : <Navigate to="/login" />} />
        <Route path="/add" element={session ? <Layout><TransactionForm /></Layout> : <Navigate to="/login" />} />
        <Route path="/edit/:id" element={session ? <Layout><TransactionForm /></Layout> : <Navigate to="/login" />} />
        <Route path="/history" element={session ? <Layout><History /></Layout> : <Navigate to="/login" />} />
        <Route path="/groups" element={session ? <Layout><Groups /></Layout> : <Navigate to="/login" />} />
        <Route path="/groups/:id" element={session ? <Layout><GroupDetail /></Layout> : <Navigate to="/login" />} />
        <Route path="/notes" element={session ? <Layout><Notes /></Layout> : <Navigate to="/login" />} />
        <Route path="/export" element={session ? <Layout><Export /></Layout> : <Navigate to="/login" />} />
        <Route path="/settings" element={session ? <Layout><Settings /></Layout> : <Navigate to="/login" />} />
        
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </Router>
  );
};

export default App;
